import React from "react";
import { useAutoRefreshStore, RefreshFrequency } from "@/shared/stores/useAutoRefreshStore";
import { Button } from "@/ui/components/ui/button";
import { RotateCw, Check } from "lucide-react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";

interface AutoRefreshModalProps {
  payload: Record<string, never>;
  onClose: () => void;
}

const REFRESH_FREQUENCIES: RefreshFrequency[] = [10, 20, 30, 40, 50, 60, "off"];

const AutoRefreshModal: React.FC<AutoRefreshModalProps> = ({ onClose }) => {
  const { t } = useTranslation("common");
  const frequency = useAutoRefreshStore((s) => s.frequency);
  const setFrequency = useAutoRefreshStore((s) => s.setFrequency);

  const handleFrequencySelect = (freq: RefreshFrequency) => {
    setFrequency(freq);
    // Auto-close modal after selection
    setTimeout(() => {
      onClose();
    }, 200);
  };

  const formatLabel = (freq: RefreshFrequency): string => {
    if (freq === "off") return t("autoRefresh.off");
    return t("autoRefresh.every", { count: Number(freq) });
  };

  return (
    <div className="flex flex-col w-full gap-4">
      <div className="flex flex-col gap-2">
        {REFRESH_FREQUENCIES.map((freq) => {
          const isSelected = freq === frequency;
          return (
            <Button
              key={freq}
              variant={isSelected ? "default" : "outline"}
              className={classNames(
                "w-full justify-between",
                isSelected && "font-semibold"
              )}
              onClick={() => handleFrequencySelect(freq)}
            >
              <span>{formatLabel(freq)}</span>
              {isSelected && <Check className="h-4 w-4" />}
            </Button>
          );
        })}
      </div>

      <div className="text-sm text-muted-foreground">
        {frequency === "off" ? (
          <p>{t("autoRefresh.disabledHint")}</p>
        ) : (
          <p>{t("autoRefresh.enabledHint", { count: Number(frequency) })}</p>
        )}
      </div>
    </div>
  );
};

export default AutoRefreshModal;
